import { EquipmentWorkflowClient } from '@frontend/equipment/api';
import { EquipmentState, IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient } from '@frontend/package/api';
import { PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { StockExtended } from '@frontend/stock/types';
import { TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useScreen } from '../useScreen';

interface ViewProps {
    product?: IProduct;
    equipment?: IEquipment;
    stock?: StockExtended;
    isProcessing: boolean;
    stopProcessing: () => void;
    canMaintain: boolean;
    removeFromStock: boolean;
    toggleRemoveFromStock: () => void;

    allStatuses: EquipmentState[];
    availableStatuses: EquipmentState[];
    selectedStatus: EquipmentState | null;
    setSelectedStatus: (status: EquipmentState) => void;

    handleSubmit: () => void;
}

const useLendingReviewResolve = (): ViewProps => {
    const { store, navigationState, onSubmit, screen_type } = useScreen();
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const auditFunctionality = getFunctionality(Application.LENDING, Feature.AUDIT_TRANSACTION);
    const auditSettings = getFunctionalitySetting(auditFunctionality?.id || '');
    const auditTrxWF = auditSettings[SettingKey.TRANSACTION_WORKFLOW_ID] as string | undefined;
    const auditPkgWF = auditSettings[SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID] as string | undefined;
    const maintainFunctionality = getFunctionality(Application.LENDING, Feature.MAINTENANCE_TRANSACTION);
    const maintainSettings = getFunctionalitySetting(maintainFunctionality?.id || '');
    const maintainTrxWF = maintainSettings[SettingKey.TRANSACTION_WORKFLOW_ID] as string | undefined;
    const maintainPkgWF = maintainSettings[SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID] as string | undefined;
    const canMaintain = !!maintainFunctionality && !!maintainTrxWF && !!maintainPkgWF;

    const [removeFromStock, setRemoveFromStock] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const product = store?.data?.product;
    const equipment = store?.data?.equipment;
    const stock = store?.data?.stock;

    const allStatuses = [
        EquipmentState.EQUIPMENT_AVAILABLE,
        EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED,
        EquipmentState.EQUIPMENT_BROKEN,
        EquipmentState.EQUIPMENT_LOST
    ];
    const availableStatuses = removeFromStock
        ? [EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED, EquipmentState.EQUIPMENT_BROKEN, EquipmentState.EQUIPMENT_LOST]
        : [EquipmentState.EQUIPMENT_AVAILABLE, EquipmentState.EQUIPMENT_MAINTENANCE_REQUIRED, EquipmentState.EQUIPMENT_BROKEN];
    const [selectedStatus, setSelectedStatus] = useState<EquipmentState | null>(availableStatuses[0]);

    const { clearData: clearPkg, localUpdatePackage } = useWizardPackage({
        store: store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    useEffect(() => {
        clearPkg();
    }, []);

    const handleSubmit = async () => {
        if (!selectedStatus || (removeFromStock && !canMaintain)) return;
        setIsProcessing(true);
        try {
            EquipmentWorkflowClient.updateEquipmentState(equipment.account_id, equipment.id, {
                state: selectedStatus.value
            });

            let trxInfo = {
                application: Application.LENDING,
                feature: Feature.AUDIT_TRANSACTION,
                state: TransactionStateName.TRANSACTION_CREATED,
                workflow_id: auditTrxWF,
                type: TransactionType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                user_id: navigationState.user?.id || undefined,
                iot_id: navigationState.iot?.id || undefined
            };

            if (removeFromStock) {
                trxInfo = {
                    ...trxInfo,
                    feature: Feature.MAINTENANCE_TRANSACTION,
                    workflow_id: maintainTrxWF,
                    type: TransactionType.LENDING
                };
            }
            const trx = await TransactionClient.postTransaction(stock.account_id, trxInfo);

            let pkgInfo = {
                workflow_id: auditPkgWF,
                user_id: trx.user_id,
                quantity: 1,
                type: PackageType.AUDIT,
                spot_id: stock.spot_id,
                module_id: stock.module_id,
                slot_id: stock.slot_id,
                product_id: product.id,
                equipment_id: equipment.id,
                priority: 1,
                blocking: false,
                feature: Feature.MAINTENANCE_TRANSACTION,
                application: Application.LENDING
            };
            if (removeFromStock) {
                pkgInfo = {
                    ...pkgInfo,
                    workflow_id: maintainPkgWF,
                    type: PackageType.PICK_UP
                };
            }
            const pkg = await PackageClient.postPackage(trx.account_id, trx.id, pkgInfo);

            await localUpdatePackage(pkg);
            await TransactionWorkflowClient.triggerTransactionState(trx.account_id, trx.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            const subFalseStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE);
            if (removeFromStock) onSubmit(subFalseStep);
            else onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        product,
        equipment,
        stock,
        isProcessing,
        stopProcessing: () => setIsProcessing(false),
        canMaintain,
        removeFromStock,
        toggleRemoveFromStock: () => {
            setSelectedStatus(null);
            setRemoveFromStock((prev) => !prev);
        },

        allStatuses,
        availableStatuses,
        selectedStatus,
        setSelectedStatus: (status: EquipmentState) => {
            if (!availableStatuses.includes(status)) return;
            setSelectedStatus(status);
        },
        handleSubmit
    };
};

export default useLendingReviewResolve;
